import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, SLAB_LABELS } from "@/api";
import { PageHeader, SlabBadge } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, FileText, Pencil } from "lucide-react";

const selCls = "w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900";

export default function QuotationNew() {
  const navigate = useNavigate();
  const { quotationId } = useParams();
  const editMode = Boolean(quotationId);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([{ product_id: "", qty: 1 }]);
  const [validDays, setValidDays] = useState(15);
  const [custom, setCustom] = useState({});
  const [qNumber, setQNumber] = useState("");
  const [quoteNo, setQuoteNo] = useState("");
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState("");
  const [taxChoice, setTaxChoice] = useState(null); // null = auto by billing state
  const [discType, setDiscType] = useState("percent");
  const [discValue, setDiscValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data.filter((b) => b.account_type !== "cash"))).catch(() => {});
    if (editMode) {
      api.get(`/quotations/${quotationId}`).then((r) => {
        const d = r.data;
        if (d.status === "converted") {
          toast.error("Note: this quotation was already converted to a tax invoice — edits here will not change that invoice");
        }
        setQNumber(d.number);
        setQuoteNo(d.number);
        setBankId(d.bank_account_id || "");
        setDiscType(d.discount_type || "percent");
        setDiscValue(d.discount_value || "");
        setTaxChoice(d.tax_type || null);
        setClientId(d.client_id);
        setItems(d.items.map((it) => ({ product_id: it.product_id, qty: it.qty })));
        setValidDays(d.valid_days || 15);
        setCustom(d.custom || {});
      }).catch(() => toast.error("Failed to load quotation"));
    }
  }, [editMode, quotationId, navigate]);

  const client = clients.find((c) => c.client_id === clientId);
  const slabKey = client ? (client.type === "b2b" ? client.category : "retail") : null;
  const interState = Boolean(client?.state && company?.state
    && client.state.trim().toLowerCase() !== company.state.trim().toLowerCase());
  const effTax = taxChoice || (interState ? "igst" : "cgst_sgst");

  const rows = useMemo(() =>
    items.map((it) => {
      const p = products.find((x) => x.product_id === it.product_id);
      if (!p || !slabKey) return { ...it, product: p, rate: 0, amount: 0, gst: 0 };
      const rate = Number(p.prices?.[slabKey] || 0);
      const amount = rate * Number(it.qty || 0);
      const gst = (amount * Number(p.gst_rate || 0)) / 100;
      return { ...it, product: p, rate, amount, gst };
    }), [items, products, slabKey]);

  const subtotal = rows.reduce((s, r) => s + r.amount, 0);
  const rawGst = rows.reduce((s, r) => s + r.gst, 0);
  const r2 = (n) => Math.round(n * 100) / 100;
  const dv = Number(discValue) || 0;
  const discAmt = dv > 0 ? r2(discType === "percent" ? subtotal * Math.min(dv, 100) / 100 : Math.min(dv, subtotal)) : 0;
  const ratio = subtotal > 0 ? (subtotal - discAmt) / subtotal : 1;
  const taxable = r2(subtotal - discAmt);
  const gstTotal = r2(rawGst * ratio);

  const setItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    setItems(next);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Select a client");
    if (items.some((it) => !it.product_id || Number(it.qty) <= 0)) return toast.error("Complete all item rows");
    setBusy(true);
    try {
      const payload = {
        client_id: clientId,
        items: items.map((it) => ({ product_id: it.product_id, qty: Number(it.qty) })),
        valid_days: Number(validDays),
        custom,
        tax_type: taxChoice || undefined,
        number: quoteNo.trim() || undefined,
        bank_account_id: bankId || null,
        discount_type: discAmt > 0 ? discType : undefined,
        discount_value: discAmt > 0 ? dv : 0,
      };
      if (editMode) {
        await api.put(`/quotations/${quotationId}`, payload);
        toast.success(`Quotation ${qNumber} updated — pricing recalculated`);
        navigate(`/quotation/${quotationId}/print`);
      } else {
        const r = await api.post("/quotations", payload);
        toast.success(`Quotation ${r.data.number} created`);
        navigate(`/quotation/${r.data.quotation_id}/print`);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `Failed to ${editMode ? "update" : "create"} quotation`);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="portal-quotation-builder" className="max-w-4xl">
      <PageHeader
        title={editMode ? `Edit Quotation ${qNumber}` : "New Proforma Invoice / Quotation"}
        sub={editMode
          ? "Saving re-prices items at current slab rates and recomputes validity"
          : "Slab pricing applies just like an invoice — no stock is deducted until converted"} />

      <form onSubmit={save} className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Quote For (Client) *</Label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="quotation-client-select" className={selCls}>
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.client_id} value={c.client_id}>
                    {c.name} — {c.type === "b2b" ? `B2B ${c.category}` : "B2C Retail"}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Valid For (days)</Label>
              <Input type="number" min="1" value={validDays} onChange={(e) => setValidDays(e.target.value)} data-testid="quotation-validity-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 border-t border-slate-100">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Quotation # (optional)</Label>
              <Input value={quoteNo} onChange={(e) => setQuoteNo(e.target.value)} placeholder={editMode ? "" : "Leave blank for auto (QT-XXXX)"}
                data-testid="quotation-number-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
              <p className="text-[11px] text-slate-500">Enter manually to override the auto number</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Bank Details on Proforma</Label>
              <select value={bankId} onChange={(e) => setBankId(e.target.value)} data-testid="quotation-bank-select" className={selCls}>
                <option value="">Don't print bank details</option>
                {banks.map((b) => (
                  <option key={b.account_id} value={b.account_id}>
                    {b.name}{b.bank ? ` — ${b.bank}` : ""}{b.account_number ? ` (A/c ${b.account_number})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Tax Treatment</Label>
            <div className="flex rounded-full border border-slate-300 overflow-hidden max-w-md" data-testid="quotation-tax-toggle">
              {[["cgst_sgst", "CGST 50% + SGST 50%"], ["igst", "IGST (Other State)"]].map(([v, l]) => {
                const active = effTax === v;
                return (
                  <button type="button" key={v}
                    onClick={() => setTaxChoice(v === (interState ? "igst" : "cgst_sgst") && taxChoice === v ? null : v)}
                    data-testid={`quotation-tax-${v}`}
                    className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors duration-200 ${active ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="quotation-tax-hint">
              {taxChoice ? "Manual override applied" : `Auto by billing state (${interState ? "other state → IGST" : "same state → CGST + SGST"})`}
            </p>
          </div>
          <div className="flex items-center gap-3" data-testid="quotation-slab-indicator">
            {slabKey ? (
              <>
                <SlabBadge slab={slabKey} />
                <span className="text-xs text-slate-500">{SLAB_LABELS[slabKey]} rates will be applied</span>
              </>
            ) : (
              <span className="text-sm text-slate-500">Select a client to lock the slab</span>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
          <div className="grid grid-cols-12 gap-2 text-xs font-mono2 uppercase tracking-wider text-slate-500 px-1">
            <span className="col-span-5">Product</span><span className="col-span-2">Qty</span>
            <span className="col-span-2 text-right">Slab Rate</span><span className="col-span-2 text-right">Amount</span><span className="col-span-1" />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quotation-item-row-${i}`}>
              <select value={r.product_id} onChange={(e) => setItem(i, "product_id", e.target.value)} data-testid={`quotation-item-product-${i}`} className={`${selCls} col-span-5`}>
                <option value="">Select product…</option>
                {products.map((p) => (
                  <option key={p.product_id} value={p.product_id}>{p.name} · stock {p.stock}</option>
                ))}
              </select>
              <Input type="number" step="any" min="0" value={r.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`quotation-item-qty-${i}`}
                className="col-span-2 bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              <div className="col-span-2 text-right font-mono2 text-sm text-slate-800">{inr(r.rate)}</div>
              <div className="col-span-2 text-right font-mono2 text-sm text-slate-900">{inr(r.amount)}</div>
              <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`quotation-item-remove-${i}`}
                className="col-span-1 text-red-600 hover:text-red-700 flex justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setItems([...items, { product_id: "", qty: 1 }])} data-testid="quotation-add-item-btn"
            className="text-xs text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add item
          </button>
        </div>

        <div className="rounded-2xl border border-emerald-500/30 bg-white p-6">
          <div className="max-w-xs ml-auto space-y-3">
            <div className="flex items-end gap-2" data-testid="quotation-discount-row">
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Discount</Label>
                <Input type="number" step="0.01" min="0" value={discValue} onChange={(e) => setDiscValue(e.target.value)} placeholder="0"
                  data-testid="quotation-discount-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
              </div>
              <div className="flex rounded-full border border-slate-300 overflow-hidden">
                {[["percent", "%"], ["flat", "₹"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setDiscType(v)} data-testid={`quotation-discount-${v}`}
                    className={`px-4 py-2 text-sm font-bold transition-colors duration-200 ${discType === v ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-2 font-mono2 text-sm max-w-xs ml-auto mt-4">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="text-slate-900" data-testid="quotation-subtotal">{inr(subtotal)}</span></div>
            {discAmt > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Less: Discount {discType === "percent" ? `(${dv}%)` : ""}</span>
                <span className="text-red-600" data-testid="quotation-discount-amount">− {inr(discAmt)}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-600"><span>Taxable value</span><span className="text-slate-900" data-testid="quotation-taxable">{inr(taxable)}</span></div>
            {effTax === "igst" ? (
              <div className="flex justify-between text-slate-600"><span>IGST (inter-state)</span><span className="text-slate-900">{inr(gstTotal)}</span></div>
            ) : (
              <>
                <div className="flex justify-between text-slate-600"><span>CGST</span><span className="text-slate-900">{inr(gstTotal / 2)}</span></div>
                <div className="flex justify-between text-slate-600"><span>SGST</span><span className="text-slate-900">{inr(gstTotal / 2)}</span></div>
              </>
            )}
            <div className="h-px bg-slate-100 my-1" />
            <div className="flex justify-between items-center">
              <span className="text-slate-700 font-sans font-medium">Quote Total</span>
              <span className="text-2xl font-bold text-emerald-600" data-testid="quotation-grand-total">{inr(taxable + gstTotal)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-mono2 uppercase tracking-[0.18em] text-slate-500 mb-4">Custom Fields (Admin-defined)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DynamicFields entity="quotations" values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
          </div>
        </div>

        <Button type="submit" disabled={busy} data-testid="quotation-create-btn"
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-12 text-base rounded-full transition-colors duration-200">
          {editMode
            ? <><Pencil className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save Changes"}</>
            : <><FileText className="w-4 h-4 mr-2" /> {busy ? "Creating…" : "Create Quotation"}</>}
        </Button>
      </form>
    </div>
  );
}
